/**
 * features/ai-assist/api.ts — AI Smart Reply / 要約 / タスク化 のクライアント API
 *
 * 対応エンドポイント (Issue #5999):
 *   POST /v1/messages/:id/smart-reply
 *   POST /v1/messages/:id/smart-reply/adopt
 *   POST /v1/rooms/:id/summarize?from=&to=
 *   POST /v1/rooms/:id/digest
 *   POST /v1/messages/:id/extract-tasks
 *
 * 実体: このリポジトリの api/ (chat-api.aile-ai.jp) Worker.
 *
 * dev では vite proxy で /api → :8787 に転送する.
 */

import { apiPost } from "@/lib/api";

export type SmartReplyTone = "polite" | "casual" | "concise";

export interface SmartReplyContextMessage {
  author: string;
  is_self: boolean;
  body: string;
  created_at: number;
}

export interface SmartReplyCandidate {
  tone: SmartReplyTone;
  body: string;
}

export interface SmartReplyResponse {
  message_id: string;
  candidates: SmartReplyCandidate[];
  model: string;
  region: string;
  latency_ms: number;
  credits_consumed: number;
}

export async function fetchSmartReply(
  messageId: string,
  body: {
    context: SmartReplyContextMessage[];
    tones?: SmartReplyTone[];
    locale?: "ja-JP" | "en-US";
  },
): Promise<SmartReplyResponse> {
  return apiPost<SmartReplyResponse>(`/v1/messages/${encodeURIComponent(messageId)}/smart-reply`, body);
}

export async function adoptSmartReply(
  messageId: string,
  candidate: SmartReplyCandidate,
): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>(`/v1/messages/${encodeURIComponent(messageId)}/smart-reply/adopt`, {
    tone: candidate.tone,
    body: candidate.body,
  });
}

export interface SummaryMessageInput {
  author: string;
  body: string;
  created_at: number;
  has_decision_marker?: boolean;
}

export interface SummarizeResponse {
  room_id: string;
  range: "day" | "week" | "all" | "important";
  paragraph: string;
  bullets: string[];
  action_items: string[];
  cited_indexes: number[];
  model: string;
  region: string;
  latency_ms: number;
  credits_consumed: number;
}

export async function fetchSummarize(
  roomId: string,
  body: {
    messages: SummaryMessageInput[];
    locale?: "ja-JP" | "en-US";
    important?: boolean;
  },
  opts: { from?: number; to?: number; range?: "day" | "week" | "all" | "important" } = {},
): Promise<SummarizeResponse> {
  const qs = new URLSearchParams();
  if (opts.from != null) qs.set("from", String(opts.from));
  if (opts.to != null) qs.set("to", String(opts.to));
  if (opts.range) qs.set("range", opts.range);
  const q = qs.toString();
  const path = `/v1/rooms/${encodeURIComponent(roomId)}/summarize${q ? `?${q}` : ""}`;
  return apiPost<SummarizeResponse>(path, body);
}

export interface DigestResponse extends SummarizeResponse {
  importance: "high" | "medium" | "low";
  period_label: string;
}

export async function fetchDigest(
  roomId: string,
  body: {
    cadence: "daily" | "weekly";
    messages: SummaryMessageInput[];
    locale?: "ja-JP" | "en-US";
  },
): Promise<DigestResponse> {
  return apiPost<DigestResponse>(`/v1/rooms/${encodeURIComponent(roomId)}/digest`, body);
}

export interface ExtractTaskMessageInput {
  message_id: string;
  author: string;
  author_id?: string;
  body: string;
  created_at: number;
}

export interface ExtractedTaskCandidate {
  title: string;
  description?: string;
  assignee_hint: string | null;
  /** assignee_hint がワークスペースの現メンバー 1 人に一致したときの user_id. 一致しなければ null */
  assignee_user_id?: string | null;
  due_date_hint: string | null;
  source_message_id: string;
  confidence: number;
}

export interface ExtractTasksResponse {
  candidates: ExtractedTaskCandidate[];
  processed_message_count: number;
  filtered_out_count: number;
  model: string;
  region: string;
  latency_ms: number;
  credits_consumed: number;
}

export async function fetchExtractTasks(
  messageId: string,
  body: {
    messages: ExtractTaskMessageInput[];
    pre_filter?: boolean;
    locale?: "ja-JP" | "en-US";
  },
): Promise<ExtractTasksResponse> {
  return apiPost<ExtractTasksResponse>(
    `/v1/messages/${encodeURIComponent(messageId)}/extract-tasks`,
    body,
  );
}
